import type { GeoJSONSource, LayerSpecification } from 'maplibre-gl';
import { useEffect, useMemo } from 'react';
import { circleToPolygon, fromPoint } from '@/lib/geo';
import { useTheme } from '@/providers/theme-provider';
import { useMap } from './map-context';
import { MAP_FONT_REGULAR, mapPalette } from './map-palette';
import type { Geofence } from '@/types/api';

const SOURCE = 'vt-geofences';
export const GEOFENCE_LAYERS = {
  fill: 'vt-geofence-fill',
  line: 'vt-geofence-line',
  label: 'vt-geofence-label',
} as const;

/** Circles are stored as centre + radius; MapLibre only fills polygons. */
export function geofenceToFeature(
  fence: Geofence,
): GeoJSON.Feature<GeoJSON.Polygon> | null {
  if (fence.shape === 'POLYGON' && fence.geometry?.coordinates?.length) {
    return {
      type: 'Feature',
      id: fence.id,
      geometry: { type: 'Polygon', coordinates: fence.geometry.coordinates },
      properties: { id: fence.id, name: fence.name, rule: fence.ruleType, active: fence.isActive },
    };
  }

  const center = fromPoint(fence.center);
  if (!center || !fence.radiusMeters) return null;
  return {
    type: 'Feature',
    id: fence.id,
    geometry: { type: 'Polygon', coordinates: circleToPolygon(center, fence.radiusMeters) },
    properties: { id: fence.id, name: fence.name, rule: fence.ruleType, active: fence.isActive },
  };
}

export function GeofenceLayer({
  geofences,
  visible,
  highlightId,
}: {
  geofences: Geofence[];
  visible: boolean;
  highlightId?: string | null;
}) {
  const { map, ready, styleEpoch } = useMap();
  const { resolved } = useTheme();

  const data = useMemo<GeoJSON.FeatureCollection<GeoJSON.Polygon>>(
    () => ({
      type: 'FeatureCollection',
      features: geofences
        .map(geofenceToFeature)
        .filter((feature): feature is GeoJSON.Feature<GeoJSON.Polygon> => feature !== null),
    }),
    [geofences],
  );

  useEffect(() => {
    if (!map || !ready) return;
    const palette = mapPalette(resolved);

    if (!map.getSource(SOURCE)) {
      map.addSource(SOURCE, { type: 'geojson', data });
    }

    const add = (spec: LayerSpecification, before?: string) => {
      if (!map.getLayer(spec.id)) {
        map.addLayer(spec, before && map.getLayer(before) ? before : undefined);
      }
    };

    // Fences sit under the fleet, so a marker is never obscured by a fill.
    add({
      id: GEOFENCE_LAYERS.fill,
      type: 'fill',
      source: SOURCE,
      paint: {
        'fill-color': palette.geofenceFill,
        'fill-opacity': ['case', ['get', 'active'], 0.09, 0.04],
      },
    });

    add({
      id: GEOFENCE_LAYERS.line,
      type: 'line',
      source: SOURCE,
      paint: {
        'line-color': palette.geofenceLine,
        'line-width': ['case', ['get', 'active'], 1.6, 1],
        // line-dasharray is not data-driven, so inactive fences are
        // distinguished by weight and opacity instead of a dash pattern.
        'line-opacity': ['case', ['get', 'active'], 0.8, 0.45],
      },
    });

    add({
      id: GEOFENCE_LAYERS.label,
      type: 'symbol',
      source: SOURCE,
      minzoom: 11,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': MAP_FONT_REGULAR,
        'text-size': 10.5,
        'text-letter-spacing': 0.02,
      },
      paint: {
        'text-color': palette.geofenceLine,
        'text-halo-color': palette.labelHalo,
        'text-halo-width': 1.4,
      },
    });
  }, [map, ready, styleEpoch, resolved, data]);

  useEffect(() => {
    if (!map || !ready) return;
    const source = map.getSource(SOURCE) as GeoJSONSource | undefined;
    source?.setData(data);
  }, [map, ready, styleEpoch, data]);

  useEffect(() => {
    if (!map || !ready) return;
    for (const id of Object.values(GEOFENCE_LAYERS)) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
  }, [map, ready, styleEpoch, visible]);

  useEffect(() => {
    if (!map || !ready || !map.getLayer(GEOFENCE_LAYERS.line)) return;
    map.setPaintProperty(GEOFENCE_LAYERS.line, 'line-width', [
      'case',
      ['==', ['get', 'id'], highlightId ?? '__none__'],
      3.2,
      ['case', ['get', 'active'], 1.6, 1],
    ]);
  }, [map, ready, styleEpoch, highlightId]);

  return null;
}
